import * as Phaser from 'phaser';
import {
  reachWorldPoints,
  type ReachAxes,
} from '../sim/boat/index.ts';
import {
  circleRimClasses,
  classifyOutline,
  complementArcs,
  type CircleRimClasses,
  type OutlineRimRuns,
} from '../sim/circleOverlap.ts';
import { BOW_OFFSET_PX } from '../sim/constants.ts';
import { pointOnCircle, type AngleArc } from '../sim/geometry.ts';
import {
  GATE_DASH_PX,
  GATE_GAP_PX,
  dashedPolyline,
  type MapDocument,
} from '../sim/map/index.ts';
import type { Vec2 } from '../sim/types.ts';

/** Land-overlap / rest-aft fill. Tunable disk colours live on `DEFAULT_AIM_CHROME`. */
export const AREA_HAZARD_COLOR = 0xe24a4a;
export const AREA_STROKE_ALPHA = 0.75;
export const PREDICTION_STROKE_ALPHA = 0.7;
export const AREA_STROKE_PX = 2.5;
/** Provisional no-wind ghost. Fill + rim only; not a click target. */
export const WIND_GHOST_FILL_ALPHA = 0.08;
export const WIND_GHOST_STROKE_ALPHA = 0.28;
/** Screen-pixel radius of the legal aiming dot (world size is `px / zoom`). */
export const HOVER_DOT_RADIUS_PX = 6;
/** Half-length of each crash-dest cross arm, same screen-fixed scale as the dot. */
export const HOVER_CROSS_ARM_PX = 8;
export const HOVER_CROSS_HALF_PX = 1.5;

function strokeArc(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  radius: number,
  arc: AngleArc,
): void {
  const span = arc.end - arc.start;
  const steps = Math.max(4, Math.ceil((Math.abs(span) / (Math.PI * 2)) * 64));

  gfx.beginPath();

  for (let i = 0; i <= steps; i += 1) {
    const point = pointOnCircle(center, radius, arc.start + (span * i) / steps);

    if (i === 0) {
      gfx.moveTo(point.x, point.y);
    } else {
      gfx.lineTo(point.x, point.y);
    }
  }

  gfx.strokePath();
}

function strokeArcs(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  radius: number,
  arcs: readonly AngleArc[],
  width: number,
  color: number,
  alpha: number,
): void {
  gfx.lineStyle(width, color, alpha);

  for (const arc of arcs) {
    strokeArc(gfx, center, radius, arc);
  }
}

function flattenArc(center: Vec2, radius: number, arc: AngleArc): Vec2[] {
  const span = arc.end - arc.start;
  const steps = Math.max(4, Math.ceil((Math.abs(span) / (Math.PI * 2)) * 64));
  const points: Vec2[] = [];

  for (let i = 0; i <= steps; i += 1) {
    points.push(pointOnCircle(center, radius, arc.start + (span * i) / steps));
  }

  return points;
}

/**
 * Planning disk over open water. The solid rim is only the open-water arcs —
 * land and under-cap water stay off this layer so the moss and the dashed
 * over-cap stroke do not double up. The fill is the full disk; opaque columns
 * sit above it and hide the slice on the grass.
 */
export function paintPlanningCircle(
  gfx: Phaser.GameObjects.Graphics,
  map: MapDocument,
  center: Vec2,
  radius: number,
  color: number,
  fillAlpha: number,
  strokeAlpha: number,
  zoom: number,
): CircleRimClasses {
  gfx.fillStyle(color, fillAlpha);
  gfx.fillCircle(center.x, center.y, radius);

  const classes = circleRimClasses(map, center, radius);
  const width = AREA_STROKE_PX / zoom;

  strokeArcs(
    gfx,
    center,
    radius,
    complementArcs([...classes.land, ...classes.underCap]),
    width,
    color,
    strokeAlpha,
  );

  return classes;
}

/**
 * Calm-disk ghost under the live wind-shifted circle. Full fill + rim, no land
 * classification, rest-aft, overland dash, or center dot.
 */
export function paintWindGhostCircle(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  radius: number,
  color: number,
  zoom: number,
): void {
  gfx.fillStyle(color, WIND_GHOST_FILL_ALPHA);
  gfx.fillCircle(center.x, center.y, radius);
  gfx.lineStyle(AREA_STROKE_PX / zoom, color, WIND_GHOST_STROKE_ALPHA);
  gfx.strokeCircle(center.x, center.y, radius);
}

/**
 * Dashed under-cap water rim above the plateau. Same cadence as the overland
 * move line. Caller owns clear / visibility; this does not paint fill.
 */
export function paintPlanningCircleOverland(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  radius: number,
  underCap: readonly AngleArc[],
  color: number,
  alpha: number,
  zoom: number,
): void {
  if (underCap.length === 0) {
    return;
  }

  const width = AREA_STROKE_PX / zoom;
  const dash = GATE_DASH_PX / zoom;
  const gap = GATE_GAP_PX / zoom;

  gfx.lineStyle(width, color, alpha);

  for (const arc of underCap) {
    const points = flattenArc(center, radius, arc);

    for (const segment of dashedPolyline(points, false, dash, gap)) {
      gfx.lineBetween(segment.a.x, segment.a.y, segment.b.x, segment.b.y);
    }
  }
}

/**
 * At rest the disk sits on the hull. Anything aft of the bow is not a legal
 * first move (same plane as `isValidDestination`), so paint that part as hazard.
 */
export function paintRestAftHalf(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  radius: number,
  heading: number,
  fillAlpha: number,
  bowOffsetPx: number = BOW_OFFSET_PX,
): void {
  gfx.fillStyle(AREA_HAZARD_COLOR, fillAlpha);

  if (bowOffsetPx >= radius) {
    gfx.fillCircle(center.x, center.y, radius);
    return;
  }

  const halfAngle = Math.acos(bowOffsetPx / radius);
  const start = heading + halfAngle;
  const sweep = Math.PI * 2 - 2 * halfAngle;
  const steps = Math.max(24, Math.ceil((sweep / Math.PI) * 24));

  for (let i = 0; i < steps; i += 1) {
    const a = pointOnCircle(center, radius, start + (sweep * i) / steps);
    const b = pointOnCircle(center, radius, start + (sweep * (i + 1)) / steps);
    gfx.fillTriangle(center.x, center.y, a.x, a.y, b.x, b.y);
  }

  const left = pointOnCircle(center, radius, heading + halfAngle);
  const right = pointOnCircle(center, radius, heading - halfAngle);
  gfx.fillTriangle(center.x, center.y, left.x, left.y, right.x, right.y);
}

/**
 * Aiming chrome at the hover dest. Drawn in local pixels about (0, 0) so the
 * scene can `setScale(1 / zoom)` and keep it screen-fixed. `legal` means the
 * boat would arrive here: a filled dest dot. A clipped crash dest (the hull
 * cannot end up on that land point) is a filled X. Colour and alpha come from
 * play-debug `aimChrome.dest`. The marker stays on the aimed dest so the
 * distance still reads as how hard you would hit; the click still commits.
 */
export function paintHoverMarker(
  gfx: Phaser.GameObjects.Graphics,
  legal: boolean,
  color: number,
  alpha: number,
): void {
  gfx.clear();
  gfx.fillStyle(color, alpha);

  if (legal) {
    gfx.fillCircle(0, 0, HOVER_DOT_RADIUS_PX);
    return;
  }

  gfx.save();
  gfx.rotateCanvas(Math.PI / 4);
  gfx.fillRect(
    -HOVER_CROSS_ARM_PX,
    -HOVER_CROSS_HALF_PX,
    HOVER_CROSS_ARM_PX * 2,
    HOVER_CROSS_HALF_PX * 2,
  );
  gfx.fillRect(
    -HOVER_CROSS_HALF_PX,
    -HOVER_CROSS_ARM_PX,
    HOVER_CROSS_HALF_PX * 2,
    HOVER_CROSS_ARM_PX * 2,
  );
  gfx.restore();
}

function strokeChain(gfx: Phaser.GameObjects.Graphics, points: readonly Vec2[]): void {
  const first = points[0];

  if (!first || points.length < 2) {
    return;
  }

  gfx.beginPath();
  gfx.moveTo(first.x, first.y);

  for (let index = 1; index < points.length; index += 1) {
    const next = points[index]!;
    gfx.lineTo(next.x, next.y);
  }

  gfx.strokePath();
}

function fillOutline(gfx: Phaser.GameObjects.Graphics, center: Vec2, points: readonly Vec2[]): void {
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index]!;
    const b = points[index + 1]!;
    gfx.fillTriangle(center.x, center.y, a.x, a.y, b.x, b.y);
  }
}

/** Planning oval. Equal axes should stay on `paintPlanningCircle` instead. */
export function paintReachArea(
  gfx: Phaser.GameObjects.Graphics,
  map: MapDocument,
  center: Vec2,
  heading: number,
  axes: ReachAxes,
  color: number,
  fillAlpha: number,
  strokeAlpha: number,
  zoom: number,
): OutlineRimRuns {
  const outline = reachWorldPoints(center, heading, axes);
  const bound = Math.max(axes.turn, axes.accel, axes.brake);

  gfx.fillStyle(color, fillAlpha);
  fillOutline(gfx, center, outline);

  const runs = classifyOutline(map, outline, center, bound);
  const width = AREA_STROKE_PX / zoom;
  gfx.lineStyle(width, color, strokeAlpha);

  for (const run of runs.water) {
    strokeChain(gfx, run);
  }

  return runs;
}

export function paintReachOverland(
  gfx: Phaser.GameObjects.Graphics,
  runs: readonly Vec2[][],
  color: number,
  alpha: number,
  zoom: number,
): void {
  if (runs.length === 0) {
    return;
  }

  const width = AREA_STROKE_PX / zoom;
  const dash = GATE_DASH_PX / zoom;
  const gap = GATE_GAP_PX / zoom;
  gfx.lineStyle(width, color, alpha);

  for (const run of runs) {
    for (const segment of dashedPolyline(run, false, dash, gap)) {
      gfx.lineBetween(segment.a.x, segment.a.y, segment.b.x, segment.b.y);
    }
  }
}

/** Full oval, no land split. Used for the calm wind ghost. */
export function paintReachGhost(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  heading: number,
  axes: ReachAxes,
  color: number,
  zoom: number,
): void {
  const outline = reachWorldPoints(center, heading, axes);
  gfx.fillStyle(color, WIND_GHOST_FILL_ALPHA);
  fillOutline(gfx, center, outline);
  gfx.lineStyle(AREA_STROKE_PX / zoom, color, WIND_GHOST_STROKE_ALPHA);
  strokeChain(gfx, outline);
}

/** Portion of the oval behind the bow plane, at rest. */
export function paintReachAft(
  gfx: Phaser.GameObjects.Graphics,
  center: Vec2,
  heading: number,
  axes: ReachAxes,
  bowOffsetPx: number,
  fillAlpha: number,
): void {
  const outline = reachWorldPoints(center, heading, axes);
  const forward = { x: Math.cos(heading), y: Math.sin(heading) };
  const bow = {
    x: center.x + forward.x * bowOffsetPx,
    y: center.y + forward.y * bowOffsetPx,
  };
  const along = (point: Vec2): number =>
    (point.x - bow.x) * forward.x + (point.y - bow.y) * forward.y;

  gfx.fillStyle(AREA_HAZARD_COLOR, fillAlpha);

  for (let index = 0; index < outline.length - 1; index += 1) {
    const a = outline[index]!;
    const b = outline[index + 1]!;
    const aAlong = along(a);
    const bAlong = along(b);
    const aAft = aAlong <= 0;
    const bAft = bAlong <= 0;

    if (aAft && bAft) {
      gfx.fillTriangle(center.x, center.y, a.x, a.y, b.x, b.y);
      continue;
    }

    if (!aAft && !bAft) {
      continue;
    }

    const span = aAlong - bAlong;
    const t = Math.abs(span) < 1e-9 ? 0 : aAlong / span;
    const cut = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    const kept = aAft ? a : b;
    gfx.fillTriangle(center.x, center.y, kept.x, kept.y, cut.x, cut.y);
  }
}
